import { motion } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useSEO, generateArticleSchema } from '../../utils/seo';

const ChushinuraPage = () => {
  // SEO設定
  useSEO({
    title: '京都山科 忠臣蔵について | 大石神社',
    description: '元禄赤穂事件、通称「忠臣蔵」は日本史上最も有名な仇討ち物語です。主君の無念を晴らすため、大石内蔵助率いる四十七士が命を賭けて成し遂げた義挙の歴史を詳しくご紹介します。松の廊下事件から討ち入りまでの経緯を年表とともに解説。',
    keywords: '忠臣蔵,赤穂事件,大石内蔵助,四十七士,討ち入り,松の廊下,吉良上野介,赤穂浪士,大石神社,京都,山科,歴史',
    canonical: '/chushingura',
    schema: generateArticleSchema(
      '忠臣蔵について - 元禄赤穂事件の歴史',
      '元禄赤穂事件、通称「忠臣蔵」の歴史を詳しく解説。松の廊下事件から討ち入りまでの経緯',
      '/chushingura',
      '2024-01-01'
    )
  });

  const timeline = [
    { year: '1701年', event: '江戸城松の廊下事件', description: '浅野内匠頭が吉良上野介に刃傷に及び、即日切腹を命じられる' },
    { year: '1701年', event: '赤穂藩お家断絶', description: '浅野家は改易となり、赤穂城は明け渡される' },
    { year: '1701-1702年', event: '大石内蔵助の決意', description: '主君の無念を晴らすため、同志を集め討ち入りを計画' },
    { year: '1702年12月14日', event: '吉良邸討ち入り', description: '四十七士が吉良邸に討ち入り、主君の仇を討つ' },
    { year: '1703年', event: '四十七士切腹', description: '幕府の命により、赤穂浪士たちは切腹を命じられる' }
  ];

  const chapters = [
    {
      title: '松の廊下事件',
      image: 'https://public.readdy.ai/ai/img_res/bad29138bc7e1d2d8197ad6153f3f039.jpg',
      content: '元禄14年（1701年）3月14日、江戸城松の廊下において、播磨国赤穂藩主・浅野内匠頭長矩が、高家旗本の吉良上野介義央に斬りかかるという事件が発生しました。浅野内匠頭は即日切腹を命じられ、赤穂藩は改易となりました。'
    },
    {
      title: '大石内蔵助の決意',
      image: 'https://readdy.ai/api/search-image?query=Portrait%20of%20determined%20samurai%20leader%20Oishi%20Kuranosuke%20in%20traditional%20Japanese%20art%20style%20contemplating%20revenge%20wearing%20formal%20kamishimo%20serious%20expression%20ukiyo-e%20woodblock%20print%20aesthetic%20historical%20edo%20period%20atmosphere&width=600&height=400&seq=chushi002&orientation=landscape',
      content: '赤穂藩家老であった大石内蔵助良雄は、主君の無念を晴らすべく、同志を集めて仇討ちを計画します。表向きは遊興に耽る姿を見せながら、密かに討ち入りの準備を進めました。この間、内蔵助の忍耐と決意は、後世に語り継がれる美談となりました。'
    },
    {
      title: '討ち入り',
      image: 'https://readdy.ai/api/search-image?query=Dramatic%20night%20scene%20of%20forty%20seven%20ronin%20samurai%20warriors%20attacking%20mansion%20in%20winter%20snow%20falling%20traditional%20Japanese%20art%20style%20ukiyo-e%20aesthetic%20warriors%20in%20armor%20with%20weapons%20historical%20revenge%20scene&width=600&height=400&seq=chushi003&orientation=landscape',
      content: '元禄15年（1702年）12月14日深夜、大石内蔵助率いる四十七士は、本所の吉良邸に討ち入りを決行。激しい戦いの末、吉良上野介の首級を挙げ、主君の仇を討ちました。その後、一同は泉岳寺へ向かい、主君の墓前に首級を供えました。'
    },
    {
      title: '義士たちの最期',
      image: 'https://readdy.ai/api/search-image?query=Solemn%20scene%20of%20samurai%20performing%20seppuku%20ritual%20in%20traditional%20Japanese%20setting%20ukiyo-e%20style%20dignified%20and%20honorable%20atmosphere%20historical%20edo%20period%20art%20peaceful%20resolution&width=600&height=400&seq=chushi004&orientation=landscape',
      content: '討ち入り後、四十七士は幕府に自首。世論は義士たちに同情的でしたが、幕府は法を重んじ、元禄16年（1703年）2月4日、四十七士に切腹を命じました。義士たちは従容として死に臨み、その忠義の心は今なお人々の心を打ち続けています。'
    }
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />
      
      <div className="pt-32 pb-24">
        <div className="max-w-6xl mx-auto px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-20"
          >
            <h1 className="text-[#D4AF37] font-serif text-6xl font-bold mb-6 tracking-wide">忠臣蔵について</h1>
            <div className="w-24 h-[1px] bg-[#D4AF37] mx-auto mb-8"></div>
            <p className="text-white text-lg leading-relaxed max-w-3xl mx-auto">
              元禄赤穂事件、通称「忠臣蔵」は、日本史上最も有名な仇討ち物語です。<br />
              主君の無念を晴らすため、四十七士が命を賭けて成し遂げた義挙は、<br />
              今なお多くの人々の心を打ち続けています。
            </p>
          </motion.div>

          <div className="space-y-24 mb-32">
            {chapters.map((chapter, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className={`grid grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'direction-rtl' : ''}`}
              >
                <div className={index % 2 === 1 ? 'order-2' : ''}>
                  <div className="border border-[#D4AF37] p-2">
                    <img 
                      src={chapter.image} 
                      alt={chapter.title}
                      className="w-full h-80 object-cover"
                    />
                  </div>
                </div>
                <div className={index % 2 === 1 ? 'order-1' : ''}>
                  <div className="inline-block px-4 py-1 bg-[#D4AF37]/20 border border-[#D4AF37] rounded-full mb-4">
                    <span className="text-[#D4AF37] text-xs tracking-wider">第{index + 1}章</span>
                  </div>
                  <h2 className="text-white font-serif text-3xl font-bold mb-6 tracking-wide">{chapter.title}</h2>
                  <p className="text-gray-400 leading-loose">{chapter.content}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-[#1A1A1A] border border-[#D4AF37]/30 rounded-lg p-12"
          >
            <h2 className="text-[#D4AF37] font-serif text-3xl font-bold mb-12 text-center tracking-wide">
              忠臣蔵年表
            </h2>
            
            <div className="relative">
              <div className="absolute left-1/2 top-0 bottom-0 w-[2px] bg-[#D4AF37]"></div>
              
              <div className="space-y-12">
                {timeline.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className={`flex items-center ${index % 2 === 0 ? 'justify-start' : 'justify-end'}`}
                  >
                    <div className={`w-5/12 ${index % 2 === 0 ? 'text-right pr-12' : 'text-left pl-12'}`}>
                      <div className="bg-black border border-[#D4AF37]/50 rounded-lg p-6">
                        <p className="text-[#D4AF37] font-bold text-xl mb-2">{item.year}</p>
                        <h3 className="text-white font-semibold text-lg mb-2">{item.event}</h3>
                        <p className="text-gray-400 text-sm leading-relaxed">{item.description}</p>
                      </div>
                    </div>
                    <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-[#D4AF37] rounded-full border-4 border-[#0A0A0A]"></div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-20 text-center"
          >
            <div className="bg-[#1A1A1A] border border-[#D4AF37]/30 rounded-lg p-10 max-w-3xl mx-auto">
              <i className="ri-information-line text-[#D4AF37] text-4xl mb-4"></i>
              <h3 className="text-white font-bold text-xl mb-4 tracking-wide">大石神社と忠臣蔵</h3>
              <p className="text-gray-400 leading-loose">
                大石神社は、大石内蔵助良雄公を御祭神としてお祀りしております。境内には義士たちの遺品や資料も展示されており、忠臣蔵の歴史を深く知ることができます。毎年12月14日の義士祭では、討ち入りの歴史を今に伝える行列が行われ、多くの参拝者で賑わいます。
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ChushinuraPage;
